import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Autocomplete,
  Button,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useAdjustmentsCore } from '../../../../core/modules/adjustments';
import { useLocationCore } from '../../../../core/modules/location';
import { useContainerCore } from '../../../../core/modules/container';
import { useCompanyCore } from '../../../../core/modules/company';
import { DestinationTypeSelector, DestinationType } from '../../shared/DestinationTypeSelector';

interface AdjustmentsFiltersProps {
  onFiltersChange?: (filters: any) => void;
}

export const AdjustmentsFilters: React.FC<AdjustmentsFiltersProps> = ({
  onFiltersChange,
}) => {
  const { filters, loadArticles, setFilters } = useAdjustmentsCore();
  
  const { locations, getLocations } = useLocationCore();
  const { containers, getContainers } = useContainerCore();
  const { companyId } = useCompanyCore();

  const [localFilters, setLocalFilters] = useState({
    // Filtros primarios (mutuamente excluyentes)
    location_id: undefined as number | undefined,
    container_id: undefined as number | undefined,
    
    // Filtros secundarios
    code: '',                  // Código del producto
    description: '',           // Descripción del producto
    // Sin proveedor en ajustes
  });

  const [destinationType, setDestinationType] = useState<DestinationType>('location');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [quickSearch, setQuickSearch] = useState({
    code: '',
    description: '',
  });

  // Type assertions

  useEffect(() => {
    // Cargar datos necesarios
    if (companyId) {
      getLocations(companyId);
      getContainers(companyId, {});
    }
    // NO cargar artículos inicialmente - solo cuando se seleccione un filtro primario
  }, [getLocations, getContainers, companyId]);

  // Manejar cambio de tipo de destino
  const handleDestinationTypeChange = (type: DestinationType) => {
    setDestinationType(type);
    // Limpiar filtros cuando se cambia el tipo
    const newFilters = {
      location_id: undefined,
      container_id: undefined,
      code: '',
      description: '',
    };
    setLocalFilters(newFilters);
    setQuickSearch({ code: '', description: '' });
    setFilters({ page: 1 });
    loadArticles(companyId!, { page: 1 });
  };

  // Filtros primarios (mutuamente excluyentes)
  const handleLocationChange = (locationId: number | null) => {
    setDestinationType('location');
    const newFilters = { 
      ...localFilters, 
      location_id: locationId || undefined,
      // Limpiar filtros de contenedor cuando se selecciona ubicación
      container_id: undefined
    };
    setLocalFilters(newFilters);

    const searchFilters = {
      ...newFilters,
      page: 1,
    };
    // Limpiar filtros undefined y vacíos
    Object.keys(searchFilters).forEach(key => {
      const value = searchFilters[key as keyof typeof searchFilters];
      if (value === undefined || value === '') {
        delete searchFilters[key as keyof typeof searchFilters];
      }
    });
    
    setFilters(searchFilters);
    loadArticles(companyId!, searchFilters);
  };

  const handleContainerChange = (containerId: number | null) => {
    setDestinationType('container');
    const newFilters = { 
      ...localFilters, 
      container_id: containerId || undefined,
      // Limpiar filtros de ubicación cuando se selecciona contenedor
      location_id: undefined
    };
    setLocalFilters(newFilters);

    const searchFilters = {
      ...newFilters,
      page: 1,
    };
    // Limpiar filtros undefined y vacíos
    Object.keys(searchFilters).forEach(key => {
      const value = searchFilters[key as keyof typeof searchFilters];
      if (value === undefined || value === '') {
        delete searchFilters[key as keyof typeof searchFilters];
      }
    });
    
    setFilters(searchFilters);
    loadArticles(companyId!, searchFilters);
  };

  // Filtros secundarios
  const handleCodeChange = (value: string) => {
    const newFilters = { ...localFilters, code: value };
    setLocalFilters(newFilters);

    // Debounce para la búsqueda
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      const searchFilters = { ...newFilters, page: 1 };
      // Limpiar filtros undefined y vacíos
      Object.keys(searchFilters).forEach(key => {
        const value = searchFilters[key as keyof typeof searchFilters];
        if (value === undefined || value === '') {
          delete searchFilters[key as keyof typeof searchFilters];
        }
      });
      setFilters(searchFilters);
      loadArticles(companyId!, searchFilters);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleDescriptionChange = (value: string) => {
    const newFilters = { ...localFilters, description: value };
    setLocalFilters(newFilters);

    // Debounce para la búsqueda
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      const searchFilters = { ...newFilters, page: 1 };
      // Limpiar filtros undefined y vacíos
      Object.keys(searchFilters).forEach(key => {
        const value = searchFilters[key as keyof typeof searchFilters];
        if (value === undefined || value === '') {
          delete searchFilters[key as keyof typeof searchFilters];
        }
      });
      setFilters(searchFilters);
      loadArticles(companyId!, searchFilters);
    }, 500);

    setSearchTimeout(timeout);
  };

  // Sin filtro de proveedor en Ajustes

  const handleClearFilters = () => {
    setDestinationType('location');
    setLocalFilters({
      location_id: undefined,
      container_id: undefined,
      code: '',
      description: '',
    });
    setQuickSearch({ code: '', description: '' });
    setFilters({ page: 1 });
    loadArticles(companyId!, { page: 1 });
  };

  // Búsqueda rápida
  const handleQuickSearchChange = (field: string, value: string) => {
    setQuickSearch(prev => ({ ...prev, [field]: value }));
    
    // Aplicar búsqueda con debounce
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      const newFilters = {
        // Mantener solo los filtros primarios activos del estado actual
        location_id: localFilters.location_id,
        container_id: localFilters.container_id,
        code: field === 'code' ? value : localFilters.code,
        description: field === 'description' ? value : localFilters.description,
        page: 1,
      };
      
      // Limpiar filtros undefined y vacíos
      Object.keys(newFilters).forEach(key => {
        const val = newFilters[key as keyof typeof newFilters];
        if (val === undefined || val === '' || val === 0) {
          delete newFilters[key as keyof typeof newFilters];
        }
      });
      
      setFilters(newFilters);
      loadArticles(companyId!, newFilters);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleQuickSearchSubmit = () => {
    const newFilters = {
      // Mantener solo los filtros primarios activos del estado actual
      location_id: localFilters.location_id,
      container_id: localFilters.container_id,
      code: quickSearch.code,
      description: quickSearch.description,
      page: 1,
    };
    
    // Limpiar filtros undefined y vacíos
    Object.keys(newFilters).forEach(key => {
      const val = newFilters[key as keyof typeof newFilters];
      if (val === undefined || val === '' || val === 0) {
        delete newFilters[key as keyof typeof newFilters];
      }
    });
    
    setFilters(newFilters);
    loadArticles(companyId!, newFilters);
  };

  const hasActiveFilters = 
    localFilters.location_id ||
    localFilters.container_id ||
    localFilters.code || 
    localFilters.description || 
    quickSearch.code ||
    quickSearch.description;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <FilterIcon color="action" />
          <Typography variant="h6">Filtros de búsqueda</Typography>
          {hasActiveFilters && (
            <Tooltip title="Limpiar filtros">
              <IconButton size="small" onClick={handleClearFilters}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Filtros primarios */}
        <Box mb={3}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
            <Box flex={1} mr={2}>
              <DestinationTypeSelector
                destinationType={destinationType}
                onDestinationTypeChange={handleDestinationTypeChange}
                selectedContainer={localFilters.container_id || null}
                onContainerChange={handleContainerChange}
                selectedLocation={localFilters.location_id || null}
                onLocationChange={handleLocationChange}
                containers={containers || []}
                locations={locations || []}
              />
            </Box>
            <Box display="flex" alignItems="center" sx={{ height: '56px' }}>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => setShowFiltersModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'medium',
                  minWidth: 160,
                  height: '56px',
                }}
              >
                Filtros Avanzados
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Modal de Filtros Avanzados */}
        <Dialog
          open={showFiltersModal}
          onClose={() => setShowFiltersModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <SearchIcon color="primary" />
              <Typography variant="h6">
                Filtros Avanzados
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Busca artículos específicos usando los filtros secundarios
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Código del artículo"
                  placeholder="SC0000010..."
                  value={quickSearch.code}
                  onChange={(e) => handleQuickSearchChange('code', e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Descripción"
                  placeholder="iPhone 14..."
                  value={quickSearch.description}
                  onChange={(e) => handleQuickSearchChange('description', e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>

              {/* Sin proveedor en ajustes */}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={() => {
                setQuickSearch({ code: '', description: '' });
                handleClearFilters();
                setShowFiltersModal(false);
              }}
              startIcon={<ClearIcon />}
              sx={{ borderRadius: 2 }}
            >
              Limpiar Todo
            </Button>
            <Button
              onClick={() => setShowFiltersModal(false)}
              sx={{ borderRadius: 2 }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                handleQuickSearchSubmit();
                setShowFiltersModal(false);
              }}
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{ borderRadius: 2 }}
            >
              Aplicar Filtros
            </Button>
          </DialogActions>
        </Dialog>

        {hasActiveFilters && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Filtros activos:
              {localFilters.location_id && 
                ` | Ubicación: ${locations?.find(loc => loc.id === localFilters.location_id)?.code}`
              }
              {localFilters.container_id && 
                ` | Contenedor: ${containers?.find(cont => cont.id === localFilters.container_id)?.code}`
              }
              {quickSearch.code && ` | Código: "${quickSearch.code}"`}
              {quickSearch.description && ` | Descripción: "${quickSearch.description}"`}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}; 